import { AlertTriangle, Clock, ListOrdered, UserCheck, Users } from 'lucide-react';
import type { TaskListItem, AdditionalTask } from '@pms/shared';
import { cn } from '@/lib/utils';
import { TaskStatusBadge, TaskTypeBadge } from './TaskBadges';
import { fullName, timeSlot } from './taskConstants';

/** Compact, clickable task row used across the By Tasks / My Tasks / employee lists. */
export function TaskRow({ task, onClick }: { task: TaskListItem; onClick?: () => void }) {
  const slot = timeSlot(task.start_time, task.end_time);
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 border-b border-border/60 px-4 py-2 text-left transition-colors last:border-b-0',
        onClick && 'hover:bg-muted/40',
      )}
    >
      {task.is_priority && <AlertTriangle className="h-4 w-4 shrink-0 text-rose-500" />}
      <div className="min-w-0 flex-1">
        {/* leading-tight on both: the default line-height adds a visible gap between the
            title and its description, which is most of the wasted height in a list row. */}
        <p className="truncate text-sm font-semibold leading-tight text-foreground">{task.title}</p>
        {task.description && <p className="truncate text-xs leading-tight text-muted-foreground">{task.description}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-muted-foreground">
          <TaskTypeBadge type={task.task_type} />
          {task.is_important && (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-400">
              <ListOrdered className="h-3 w-3" />Important
            </span>
          )}
          {slot && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{slot}</span>}
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {task.assignee_count} {task.is_important ? 'in sequence' : `assignee${task.assignee_count === 1 ? '' : 's'}`}
          </span>
          {task.is_important && (
            task.active_assignee_id ? (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <UserCheck className="h-3 w-3" />
                with {fullName(task.active_assignee_first_name, task.active_assignee_last_name)}
              </span>
            ) : (
              <span className="text-rose-600 dark:text-rose-400">no one available</span>
            )
          )}
        </div>
      </div>
      <TaskStatusBadge status={task.display_status} className="shrink-0" />
    </button>
  );
}

function minutesToLabel(mins: number): string {
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function AdditionalTaskRow({ item }: { item: AdditionalTask }) {
  return (
    <div className="border-b border-border/60 px-4 py-2 last:border-b-0">
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0 flex-1">
          {/* Kept in step with TaskRow above — the two sit in sibling tabs. */}
          <p className="truncate text-sm font-semibold leading-tight text-foreground">{item.title}</p>
          {item.description && <p className="truncate text-xs leading-tight text-muted-foreground">{item.description}</p>}
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-muted-foreground">
            <span>{new Date(item.work_date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{item.start_time.slice(0, 5)}–{item.end_time.slice(0, 5)}</span>
            {item.assigned_by_first_name && (
              <span>by {fullName(item.assigned_by_first_name, item.assigned_by_last_name)}</span>
            )}
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
          {minutesToLabel(item.duration_minutes)}
        </span>
      </div>
    </div>
  );
}
